import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';
import { formatStorageBytes, formatStorageMb } from '@/lib/getStorageUsage';
import type { TenantInfo } from '../page';

interface Props {
  tenant: TenantInfo;
  storageBytes: number;
  onTenantChange: (t: TenantInfo) => void;
}

const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#2563eb' };

const STORAGE_TIERS = [
  { label: '512 MB',  value: 512 },
  { label: '1 GB',    value: 1024 },
  { label: '5 GB',    value: 5120 },
  { label: '10 GB',   value: 10240 },
  { label: '50 GB',   value: 51200 },
  { label: '100 GB',  value: 102400 },
  { label: '500 GB',  value: 512000 },
];

export default function StorageTab({ tenant, storageBytes, onTenantChange }: Props) {
  const usedMb  = storageBytes / (1024 * 1024);
  const pct     = tenant.storage_quota_mb > 0
    ? Math.min(100, Math.round((usedMb / tenant.storage_quota_mb) * 100))
    : 0;
  const barColor = pct > 80 ? 'bg-red-400' : pct > 50 ? 'bg-amber-400' : 'bg-emerald-400';
  const textColor = pct > 80 ? 'text-red-600' : pct > 50 ? 'text-amber-600' : 'text-emerald-600';

  const handleEditQuota = async () => {
    const { value: newQuota } = await Swal.fire({
      title: 'Editar storage quota',
      html: `
        <p style="color:#64748b;font-size:13px;margin-bottom:12px">Quota actual: <strong>${formatStorageMb(tenant.storage_quota_mb)}</strong></p>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px">
          ${STORAGE_TIERS.map((t) => `
            <button type="button" onclick="document.getElementById('swal2-input').value='${t.value}';document.querySelectorAll('[data-tier]').forEach(b=>b.style.background='#f8fafc');this.style.background='#dbeafe'"
              data-tier="${t.value}"
              style="padding:6px 8px;border:1px solid #e2e8f0;border-radius:8px;font-size:12px;cursor:pointer;background:${t.value === tenant.storage_quota_mb ? '#dbeafe' : '#f8fafc'}">
              ${t.label}
            </button>
          `).join('')}
        </div>
      `,
      input: 'number',
      inputLabel: 'O ingresa un valor personalizado (MB)',
      inputValue: tenant.storage_quota_mb,
      inputAttributes: { min: '128', step: '128' },
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      ...SW,
    });
    if (!newQuota || Number(newQuota) === tenant.storage_quota_mb) return;
    const { error } = await supabase.from('saas_tenants')
      .update({ storage_quota_mb: Number(newQuota), updated_at: new Date().toISOString() })
      .eq('id', tenant.id);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    onTenantChange({ ...tenant, storage_quota_mb: Number(newQuota) });
    Swal.fire({ title: 'Quota actualizada', icon: 'success', timer: 1300, showConfirmButton: false, ...SW });
  };

  const freeBytes = Math.max(0, (tenant.storage_quota_mb * 1024 * 1024) - storageBytes);

  return (
    <div className="space-y-5 max-w-2xl">

      {/* Main usage card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 text-slate-500">
              <i className="ri-hard-drive-line text-lg"></i>
            </span>
            <div>
              <p className="text-slate-900 text-base font-semibold">VPS Storage</p>
              <p className="text-slate-400 text-xs">Almacenamiento del tenant en servidor</p>
            </div>
          </div>
          <button
            onClick={handleEditQuota}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-edit-line"></i></span>
            Editar quota
          </button>
        </div>

        {/* Big numbers */}
        <div className="grid grid-cols-3 gap-4 mb-5">
          <div>
            <p className="text-slate-400 text-xs mb-1">Utilizado</p>
            <p className={`text-2xl font-bold ${textColor}`}>{formatStorageBytes(storageBytes)}</p>
          </div>
          <div>
            <p className="text-slate-400 text-xs mb-1">Quota total</p>
            <p className="text-2xl font-bold text-slate-700">{formatStorageMb(tenant.storage_quota_mb)}</p>
          </div>
          <div>
            <p className="text-slate-400 text-xs mb-1">Disponible</p>
            <p className="text-2xl font-bold text-slate-400">{formatStorageBytes(freeBytes)}</p>
          </div>
        </div>

        {/* Bar */}
        <div className="w-full bg-slate-100 rounded-full h-3 mb-2 overflow-hidden">
          <div
            className={`h-3 rounded-full transition-all ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-400">0</span>
          <span className={`font-bold ${textColor}`}>{pct}% utilizado</span>
          <span className="text-slate-400">{formatStorageMb(tenant.storage_quota_mb)}</span>
        </div>
      </div>

      {/* Status card */}
      <div className={`rounded-xl border p-4 flex items-start gap-3 ${pct > 80 ? 'bg-red-50 border-red-200' : pct > 50 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
        <span className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 ${pct > 80 ? 'bg-red-100 text-red-600' : pct > 50 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
          <i className={`${pct > 80 ? 'ri-error-warning-line' : pct > 50 ? 'ri-alert-line' : 'ri-checkbox-circle-line'} text-sm`}></i>
        </span>
        <div>
          <p className={`text-sm font-semibold ${pct > 80 ? 'text-red-700' : pct > 50 ? 'text-amber-700' : 'text-emerald-700'}`}>
            {pct > 80 ? '⚠️ Storage crítico' : pct > 50 ? 'Storage al 50%+' : 'Storage OK'}
          </p>
          <p className={`text-xs mt-0.5 ${pct > 80 ? 'text-red-600' : pct > 50 ? 'text-amber-600' : 'text-emerald-600'}`}>
            {pct > 80
              ? 'El tenant está cerca de su límite. Considera aumentar la quota.'
              : pct > 50
              ? 'Más de la mitad del storage está en uso.'
              : 'El tenant tiene suficiente espacio disponible.'}
          </p>
        </div>
      </div>

      {/* Tiers reference */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <p className="text-slate-400 text-xs uppercase tracking-wider font-medium">Tiers de storage disponibles</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4">
          {STORAGE_TIERS.map((tier, idx) => (
            <button
              key={tier.value}
              onClick={() => {
                Swal.fire({
                  title: `¿Cambiar quota a ${tier.label}?`,
                  icon: 'question', showCancelButton: true,
                  confirmButtonText: 'Sí, cambiar', cancelButtonText: 'Cancelar', ...SW,
                }).then(async ({ isConfirmed }) => {
                  if (!isConfirmed) return;
                  const { error } = await supabase.from('saas_tenants')
                    .update({ storage_quota_mb: tier.value, updated_at: new Date().toISOString() })
                    .eq('id', tenant.id);
                  if (!error) {
                    onTenantChange({ ...tenant, storage_quota_mb: tier.value });
                    Swal.fire({ title: `Quota → ${tier.label}`, icon: 'success', timer: 1300, showConfirmButton: false, ...SW });
                  }
                });
              }}
              className={`flex items-center justify-between px-4 py-3 text-sm transition-colors cursor-pointer ${
                tier.value === tenant.storage_quota_mb
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-slate-600 hover:bg-slate-50'
              } ${idx % 4 !== 3 ? 'border-r border-slate-100' : ''} ${idx >= 4 ? 'border-t border-slate-100' : ''}`}
            >
              <span>{tier.label}</span>
              {tier.value === tenant.storage_quota_mb && (
                <span className="w-4 h-4 flex items-center justify-center text-blue-600">
                  <i className="ri-check-line text-xs"></i>
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Architecture note */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <div className="flex items-start gap-2.5">
          <span className="w-5 h-5 flex items-center justify-center text-slate-400 shrink-0 mt-0.5">
            <i className="ri-information-line text-sm"></i>
          </span>
          <div>
            <p className="text-slate-500 text-xs font-medium mb-1">Arquitectura de almacenamiento</p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Los archivos del tenant (fotos, videos, ZIPs, álbumes) se almacenan en el VPS bajo
              <code className="text-slate-600 font-mono text-[11px] mx-1">/storage/{tenant.slug}/</code>.
              La quota es el límite máximo permitido por este tenant.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
